import React, { memo, useState, useMemo, useCallback, useRef, useEffect } from 'react';

import { Text, Spacer, SearchBar, Divider } from '@Sage/hoi-poi-ui';
import { removeAccents } from '@Sage/ssm-utils';
import classnames from 'classnames';
import Fuse from 'fuse.js';

import AbsoluteEmpty from 'components/AbsoluteEmpty';
import AccessibilityIllustration from 'components/illustrations/AccessibilityIllustration';
import { getLiteral } from 'utils/getLiteral';
import { stripDiacritics } from 'utils/strings';

const fuseOptions = {
    threshold: 0.2,
    keys: ['label'],
    getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path);
        return removeAccents(value?.trim?.());
    },
};

import SelectHoi from 'components/SelectHoi';

const AdvancedSelectMenu = memo(
    ({
        menuTitle,
        options,
        filterOptions,
        defaultFilter,
        searchPlaceholder,
        filterPlaceholder,
        onChange,
        value,
        visible,
    }) => {
        // Determine initial filter based on current value or defaultFilter
        const getInitialFilter = useCallback(() => {
            if (value?.value && options) {
                // Find which filter category contains the current value
                const categoryWithValue = options.find((category) =>
                    category.options?.some((opt) => opt.value === value.value),
                );
                if (categoryWithValue) {
                    return categoryWithValue.key;
                }
            }
            return defaultFilter;
        }, [value, options, defaultFilter]);

        const [filter, setFilter] = useState(getInitialFilter);
        const [searchInput, setSearchInput] = useState();
        const debounce = useRef(null);
        const searchBarRef = useRef(null);
        const latestRawSearchRef = useRef('');

        const filteredOptions = useMemo(() => {
            let filtered = options?.filter((option) => filter && option.key === filter);

            if (searchInput) {
                filtered = filtered.map((section) => {
                    const fuse = new Fuse(section.options, fuseOptions);
                    return {
                        ...section,
                        options: fuse.search(searchInput).map((result) => result.item),
                    };
                });
            }

            return filtered?.filter((option) => option?.options?.length > 0);
        }, [filter, options, searchInput]);

        // SearchBar → Select uses react-select: typing fires onInputChange, not onChange.
        // Parent onChange is only for option pick; wire search via customOnChangeInput (see EmojiPicker / OpportunityPopover).
        const customOnChangeInput = useCallback(
            ({ inputValue: text, action, setNewInputValue }) => {
                if (action.action === 'input-change') {
                    setNewInputValue(text ?? '');
                    latestRawSearchRef.current = text ?? '';
                    clearTimeout(debounce.current);
                    debounce.current = setTimeout(() => {
                        const raw = latestRawSearchRef.current;
                        const rawText = raw ? stripDiacritics(raw.toLowerCase().trim()) : '';
                        setSearchInput(rawText || undefined);
                    }, 250);
                    return;
                }
                // Blur, Enter (forceBlurOnEnter), menu-close, etc. — flush debounced filter so short queries still apply.
                clearTimeout(debounce.current);
                const raw = latestRawSearchRef.current;
                const rawText = raw ? stripDiacritics(raw.toLowerCase().trim()) : '';
                setSearchInput(rawText || undefined);
            },
            [],
        );

        const filteredOptionsMap = useMemo(() => {
            return filteredOptions?.map((option) => (
                <>
                    <Text key={option.key} className="fm-advanced-select-menu__section" medium>
                        {option.label}
                    </Text>
                    {option?.options?.map((option) => (
                        <Text
                            key={option.value}
                            className={classnames('fm-advanced-select-menu__option', {
                                'fm-advanced-select-menu__option--selected':
                                    value?.value === option.value,
                            })}
                            onClick={() => onChange(option)}
                        >
                            {option.label}
                        </Text>
                    ))}
                </>
            ));
        }, [filteredOptions, onChange, value]);

        const getRef = useCallback((ref) => {
            searchBarRef.current = ref;
        }, []);

        useEffect(() => {
            if (visible) searchBarRef.current?.focus();
        }, [visible]);

        return (
            <div className="fm-advanced-select-menu">
                <div className="fm-advanced-select-menu__header">
                    <Text className="fm-advanced-select-menu__title" type="subtitle1" medium>
                        {menuTitle}
                    </Text>
                    <Spacer y={2} />
                    {filterOptions?.length > 0 && (
                        <SelectHoi
                            placeholder={filterPlaceholder}
                            options={filterOptions}
                            onChange={setFilter}
                            value={filter}
                            isFullWidth
                            usePlainValue
                        />
                    )}
                    <SearchBar
                        className="fm-automation-settings__search-bar"
                        placeholder={searchPlaceholder}
                        inputValue={searchInput}
                        customOnChangeInput={customOnChangeInput}
                        isFullWidth
                        useAsSimpleSearch
                        getRef={getRef}
                    />
                </div>
                <Divider />
                {filteredOptionsMap?.length > 0 ? (
                    <div className="fm-advanced-select-menu__options">{filteredOptionsMap}</div>
                ) : (
                    <div className="fm-advanced-select-menu__empty">
                        <AbsoluteEmpty
                            svg={<AccessibilityIllustration />}
                            title={getLiteral('label_automations_insert_field_not_found')}
                            subtitle={getLiteral('label_automations_insert_field_not_found_desc')}
                            size="popover"
                        />
                    </div>
                )}
            </div>
        );
    },
);

export default AdvancedSelectMenu;
